/**
 * The large-CSV grid's data layer (#981 stage 1, spec §7): the `/api/csv/:fileId/…` routes, through
 * `request` from `@papan/api-client` as the database grid's adapter does — so a share-view session's
 * auth rides along exactly as it does for `/api/sqlite`.
 *
 * - A working copy, view or search that is still being built answers `building` + a job id; the
 *   adapter polls `/job/:jobId` until it is ready (reporting `progress`, 0..1) and asks again.
 * - Every read carries the content hash `open` returned. A `409 stale` (the file changed under the
 *   grid) re-opens — the new summary goes to `onReopen` — and retries once.
 * - A version preview passes `versionId`; every route then reads that version (`?version=`).
 */
import { request } from '@papan/api-client'

/** The server's answer for a working copy that is ready. */
export interface LargeCsvOpenReady {
  status: 'ready'
  contentHash: string
  header: string[]
  totalRows: number
  columnCount: number
  delimiter: string
  encoding: string
  /** #981 stage 2: the manifest's fragments in file order (the client maps display rows onto them). */
  fragments?: { id: string; rows: number }[]
  /** The current content already holds unsaved edits. */
  dirty?: boolean
  /** Unsaved edits kept on EARLIER contents of the file (spec §8.5): offer to save them as a copy or discard them. */
  staleEdits?: { base: string; fragments: number }[]
}

export interface LargeCsvRow {
  /** The row's global index in the base order. */
  row: number
  fragId: string
  src: number
  fields: string[]
}

/** The filter subset the server takes (spec §7.1) — mirrors `CsvViewSpec` in the server's views.ts. */
export type LargeCsvTextFilter = { type: 'contains' | 'equals' | 'startsWith' | 'notBlank'; value?: string }
export type LargeCsvNumberFilter = { type: 'eq' | 'lt' | 'gt' | 'between'; value: number; valueTo?: number }
export interface CsvViewSpec {
  sort?: { col: number; dir: 'asc' | 'desc' }[]
  filter?: Record<number, LargeCsvTextFilter | LargeCsvNumberFilter>
  quickSearch?: string
}

export interface LargeCsvCell {
  row: number
  col: number
}

export type LargeCsvSearchResult =
  | { hits: LargeCsvCell[]; total: number; totalCapped: boolean }
  | { failed: 'error' }

export type LargeCsvOpenResult = LargeCsvOpenReady | { failed: 'unparseable' | 'error' }

/** Called while a build / view / search job runs (`progress` 0..1). */
export type LargeCsvProgress = (progress: number) => void

export interface LargeCsvAdapter {
  open(onProgress?: LargeCsvProgress): Promise<LargeCsvOpenResult>
  /** Rows `[start, start + count)` of the base order, or of a view. `count` ≤ 1000. */
  rows(start: number, count: number, viewId?: string): Promise<LargeCsvRow[]>
  view(spec: CsvViewSpec, onProgress?: LargeCsvProgress): Promise<{ viewId: string; rowCount: number } | { failed: 'error' }>
  /**
   * #981 Task 9 — after a save that moved no row (`sameRows`): the view of `spec` as the base `fromHash` built it,
   * served for the current hash (the old order over the new rows) under a provisional id that `rows` reads. Null when
   * the server has none (no record, the old view gone, an older server): the caller waits for the real view. Never a job.
   * Optional: an adapter without it has no provisional views.
   */
  provisionalView?(spec: CsvViewSpec, fromHash: string): Promise<{ viewId: string; rowCount: number } | null>
  /** The next matching cells after `from` (row-major, base order), and the capped total. */
  search(q: string, from?: LargeCsvCell, onProgress?: LargeCsvProgress): Promise<LargeCsvSearchResult>
  /** Whether a whole-file collab room holds unsaved edits (the drive then mounts the old editor). */
  hasUnsavedWholeFileEdits(): Promise<boolean>
  /** Both answers of `GET legacy-edits`: unsaved whole-file edits (keep the old editor) and unsaved large-grid edits
   *  — fragment rows on any base — which keep a csv/tsv in the large grid whatever its size (#981 QA 14). */
  legacyEdits(): Promise<LargeCsvLegacyEdits>
  /** From `saving` until `rebase`/`release`: a 409 stale rejects with LargeCsvSwapPendingError (no reopen, no onReopen). */
  holdForSave(): void
  /** Drops the hold without rebasing (a failed save / discard keeps the same hash). */
  release(): void
  /** Re-open (the new hash after a save); clears the hold; never calls onReopen. */
  rebase(): Promise<LargeCsvOpenResult>
  /** The hash reads currently carry. */
  currentHash(): string | null
}

export interface LargeCsvLegacyEdits { hasUnsavedWholeFileEdits: boolean; hasFragmentEdits: boolean }

/** A read saw `stale` while the caller's own save is landing; the caller re-reads it after the swap. */
export class LargeCsvSwapPendingError extends Error {
  constructor() {
    super('large csv: a save is landing')
    this.name = 'LargeCsvSwapPendingError'
  }
}

export interface LargeCsvAdapterOptions {
  versionId?: string
  /** A re-open after the file changed: the grid resets to the new summary. */
  onReopen?: (ready: LargeCsvOpenReady) => void
  /** Poll interval for a running job (ms). */
  pollMs?: number
}

/** The most rows one `rows` call may ask for (the server's cap). */
export const LARGE_CSV_MAX_ROWS_PER_READ = 1000

type JobAnswer = { status: 'building'; jobId: string; progress?: number } | { status: 'failed'; reason?: 'unparseable' | 'error' }

function isStale(err: unknown): boolean {
  const e = err as { status?: number; message?: string } | null
  return e?.status === 409 && e.message === 'stale'
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export function createLargeCsvAdapter(fileId: string, opts: LargeCsvAdapterOptions = {}): LargeCsvAdapter {
  const base = `/api/csv/${encodeURIComponent(fileId)}`
  const pollMs = opts.pollMs ?? 500
  let hash: string | null = null
  let reopening: Promise<LargeCsvOpenResult> | null = null
  let holding = false
  /** Bumped by `rebase`: an `open` that started in an older generation never assigns `hash`. */
  let gen = 0

  /** A query string: the version (always, when one is previewed) plus `params`. */
  const qs = (params: Record<string, string | number | undefined> = {}) => {
    const p = new URLSearchParams()
    if (opts.versionId) p.set('version', opts.versionId)
    for (const [k, v] of Object.entries(params)) if (v !== undefined) p.set(k, String(v))
    const s = p.toString()
    return s ? `?${s}` : ''
  }

  /** Polls a job until it is ready; false when it failed (`reason` says why). */
  async function waitForJob(jobId: string, onProgress?: LargeCsvProgress): Promise<{ ok: true } | { ok: false; reason: 'unparseable' | 'error' }> {
    for (;;) {
      await sleep(pollMs)
      const st = await request<{ status: string; progress?: number; reason?: 'unparseable' | 'error' }>(`${base}/job/${encodeURIComponent(jobId)}${qs()}`)
      if (st.status === 'ready') {
        onProgress?.(1)
        return { ok: true }
      }
      if (st.status === 'failed') return { ok: false, reason: st.reason ?? 'error' }
      onProgress?.(st.progress ?? 0)
    }
  }

  async function open(onProgress?: LargeCsvProgress): Promise<LargeCsvOpenResult> {
    const myGen = gen
    for (;;) {
      const res = await request<LargeCsvOpenReady | JobAnswer>(`${base}/open${qs()}`, { method: 'POST' })
      if (res.status === 'ready') {
        if (myGen === gen) hash = res.contentHash
        return res
      }
      if (res.status === 'failed') return { failed: res.reason ?? 'error' }
      onProgress?.(res.progress ?? 0)
      const done = await waitForJob(res.jobId, onProgress)
      if (!done.ok) return { failed: done.reason }
      // ready: ask again for the summary
    }
  }

  /** One re-open shared by every read that saw `stale` at once. */
  async function reopen(): Promise<boolean> {
    if (!reopening) {
      reopening = open().finally(() => { reopening = null })
      const myGen = gen
      const r = await reopening
      if ('failed' in r) return false
      // A hold or a rebase since this started supersedes it: the caller retries with the current hash.
      if (!holding && myGen === gen) opts.onReopen?.(r)
      return true
    }
    const r = await reopening
    return !('failed' in r)
  }

  /** Runs a read that needs the hash; a `stale` answer re-opens and retries once. */
  async function withHash<T>(fn: (h: string) => Promise<T>): Promise<T> {
    if (!hash) {
      const r = await open()
      if ('failed' in r) throw new Error(`large csv: open failed (${r.failed})`)
    }
    const sent = hash!
    try {
      return await fn(sent)
    } catch (err) {
      if (!isStale(err)) throw err
      // Already rebased / reopened since this read was sent: just retry with the current hash.
      if (hash !== sent) return fn(hash!)
      if (holding) throw new LargeCsvSwapPendingError()
      if (!(await reopen())) throw err
      return fn(hash!)
    }
  }

  const legacyEdits = async (): Promise<LargeCsvLegacyEdits> => {
    try {
      const res = await request<{ hasUnsavedWholeFileEdits?: boolean; hasFragmentEdits?: boolean }>(`${base}/legacy-edits`)
      return { hasUnsavedWholeFileEdits: res.hasUnsavedWholeFileEdits === true, hasFragmentEdits: res.hasFragmentEdits === true }
    } catch (err) {
      // The route arrives with stage 2 (#981 Task 14); until then there is nothing to report.
      if ((err as { status?: number } | null)?.status === 404) return { hasUnsavedWholeFileEdits: false, hasFragmentEdits: false }
      throw err
    }
  }
  return {
    open,

    holdForSave() { holding = true },
    release() { holding = false },
    /** The hold is cleared even when the re-open fails (a `failed` result or a throw). */
    async rebase() {
      // Hold from the start: a client that never saw `saving` has no hold yet, and a read meeting
      // `stale` mid-rebase must not start its own reopen (it would remount the grid from the top).
      holding = true
      gen++
      try {
        return await open()
      } finally {
        holding = false
      }
    },
    currentHash: () => hash,

    async rows(start, count, viewId) {
      if (!Number.isSafeInteger(start) || start < 0) throw new RangeError('rows: start must be a non-negative integer')
      if (!Number.isSafeInteger(count) || count < 0 || count > LARGE_CSV_MAX_ROWS_PER_READ) {
        throw new RangeError(`rows: count must be 0..${LARGE_CSV_MAX_ROWS_PER_READ}`)
      }
      return withHash(async (h) => {
        const res = await request<{ rows: LargeCsvRow[] }>(`${base}/rows${qs({ start, count, view: viewId, hash: h })}`)
        return res.rows
      })
    },

    view(spec, onProgress) {
      return withHash(async (h) => {
        for (;;) {
          const res = await request<{ status: 'ready'; viewId: string; rowCount: number } | JobAnswer>(`${base}/views${qs()}`, {
            method: 'POST',
            body: JSON.stringify({ ...spec, hash: h }),
          })
          if (res.status === 'ready') return { viewId: res.viewId, rowCount: res.rowCount }
          if (res.status === 'failed') return { failed: 'error' as const }
          onProgress?.(res.progress ?? 0)
          const done = await waitForJob(res.jobId, onProgress)
          if (!done.ok) return { failed: 'error' as const }
        }
      })
    },

    async provisionalView(spec, fromHash) {
      try {
        return await withHash(async (h) => {
          const res = await request<{ status: string; viewId?: string; rowCount?: number; provisional?: boolean }>(`${base}/views${qs()}`, {
            method: 'POST',
            body: JSON.stringify({ ...spec, hash: h, provisionalFrom: fromHash }),
          })
          // only a provisional answer: a server that ignores `provisionalFrom` would answer (or start) the real view
          return res.status === 'ready' && res.provisional === true && typeof res.viewId === 'string' && typeof res.rowCount === 'number'
            ? { viewId: res.viewId, rowCount: res.rowCount }
            : null
        })
      } catch {
        return null
      }
    },

    search(q, from, onProgress) {
      return withHash(async (h) => {
        for (;;) {
          const res = await request<{ status: 'ready'; hits: LargeCsvCell[]; total: number; totalCapped: boolean } | JobAnswer>(
            `${base}/search${qs({ q, fromRow: from?.row, fromCol: from?.col, hash: h })}`,
          )
          if (res.status === 'ready') return { hits: res.hits, total: res.total, totalCapped: res.totalCapped }
          if (res.status === 'failed') return { failed: 'error' as const }
          onProgress?.(res.progress ?? 0)
          const done = await waitForJob(res.jobId, onProgress)
          if (!done.ok) return { failed: 'error' as const }
        }
      })
    },

    async hasUnsavedWholeFileEdits() {
      return (await legacyEdits()).hasUnsavedWholeFileEdits
    },
    legacyEdits,
  }
}
